import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  Check,
  CheckSquare,
  ClipboardList,
  Download,
  Heart,
  HeartPulse,
  Mic,
  Moon,
  Pill,
  Gauge,
  RefreshCw,
  Send,
  Star,
  Stethoscope,
  Trash2,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';

export function CompletionCircle({ completed = false, highlight = false }) {
  return (
    <button
      type="button"
      aria-hidden
      className={`mt-0.5 shrink-0 rounded-full border-2 p-0.5 transition-all ${
        highlight ? 'scale-110 ring-2 ring-primary/30' : ''
      }`}
      style={{
        width: 18,
        height: 18,
        borderColor: completed ? '#1E40AF' : '#D1D5DB',
        background: completed ? '#1E40AF' : 'transparent',
      }}
    >
      {completed ? (
        <Check className="h-3 w-3 text-white" strokeWidth={3} />
      ) : (
        <span className="block h-3 w-3" />
      )}
    </button>
  );
}

const TASK_ICON_BG = {
  vitals: 'bg-green-100 text-[#16A34A]',
  medication_admin: 'bg-rose-100 text-[#E11D48]',
  manual_default: 'bg-gray-100 text-gray-500',
  growth: 'bg-green-100 text-[#16A34A]',
};

const TASK_ICONS = {
  vitals: HeartPulse,
  medication_admin: Pill,
  manual_default: CheckSquare,
  growth: TrendingUp,
};

export function DemoTaskTabs({ active }) {
  const tabs = [
    { id: 'pending', label: 'Pending' },
    { id: 'completed', label: 'Completed' },
    { id: 'flowsheet', label: 'Flow Sheet' },
  ];

  return (
    <div className="flex rounded-xl border border-gray-200 bg-white p-1">
      {tabs.map((tab) => (
        <div
          key={tab.id}
          className={`flex-1 rounded-lg px-0.5 py-1.5 text-center text-[8px] font-semibold transition-colors ${
            active === tab.id ? 'bg-primary text-white' : 'text-gray-600'
          }`}
        >
          {tab.label}
        </div>
      ))}
    </div>
  );
}

export function DemoTaskCard({
  iconKey,
  title,
  nestedItems = [],
  completed = false,
  highlight = false,
  showTap = false,
}) {
  const Icon = TASK_ICONS[iconKey] || CheckSquare;
  const iconBg = TASK_ICON_BG[iconKey] || TASK_ICON_BG.manual_default;

  return (
    <div
      className={`relative flex items-start gap-2 rounded-xl border border-gray-200 bg-white p-2 transition-all ${
        highlight ? 'border-primary-ring' : ''
      }`}
      style={{ opacity: completed ? 0.65 : 1 }}
    >
      {showTap && (
        <span className="onboarding-pulse-ring-primary pointer-events-none absolute -right-0.5 -top-0.5 z-10 h-5 w-5 rounded-full" />
      )}
      <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
        <Icon className="h-3.5 w-3.5" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-[10px] font-bold leading-tight text-gray-900 ${completed ? 'line-through' : ''}`}>
          {title}
        </p>
        {nestedItems.length > 0 ? (
          <div className="relative ml-0.5 mt-0.5 border-l border-gray-200 pl-2.5">
            {nestedItems.map((item) => (
              <div key={item.label} className="relative flex items-start justify-between gap-2 py-0.5">
                <span className="absolute -left-2.5 top-2 h-px w-2 bg-gray-200" aria-hidden />
                <div className="min-w-0">
                  <p
                    className={`text-[9px] font-medium leading-tight text-gray-800 ${
                      item.completed ? 'line-through text-gray-500' : ''
                    }`}
                  >
                    {item.label}
                  </p>
                  {!item.completed && item.due && (
                    <p className="text-[8px] leading-tight text-gray-500">{item.due}</p>
                  )}
                  {item.completed && (
                    <p className="text-[9px] text-gray-500">Completed via Vitals recording</p>
                  )}
                </div>
                <CompletionCircle completed={item.completed} highlight={item.highlight} />
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-0.5 flex items-start justify-between gap-1.5">
            <p className="text-[8px] leading-tight text-gray-500">
              {completed ? 'Completed manually' : 'Due in 2h'}
            </p>
            <CompletionCircle completed={completed} highlight={highlight} />
          </div>
        )}
      </div>
    </div>
  );
}

export function DemoPatientHeader({ name, room, noteType }) {
  return (
    <div className="shrink-0 border-b border-gray-200 bg-white px-3 py-2.5">
      <p className="truncate text-[12px] font-bold text-gray-900">{name}</p>
      <p className="text-[9px] text-gray-500">
        Room {room}
        {noteType ? ` · ${noteType}` : ''}
      </p>
    </div>
  );
}

export function DemoNoteTypeHeader({ noteType, compact = false }) {
  const config = {
    progress_note: { icon: BookOpen, label: 'Progress Note' },
    vitals: { icon: HeartPulse, label: 'Vitals' },
    medication_admin: { icon: Pill, label: 'Medication Administration' },
  }[noteType];

  if (!config) return null;
  const Icon = config.icon;

  return (
    <div className={`mb-1.5 flex items-center gap-1.5 ${compact ? 'mb-1' : ''}`}>
      <Icon className={`shrink-0 text-primary ${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} strokeWidth={2} />
      <h2 className={`font-bold text-gray-900 ${compact ? 'text-[10px]' : 'text-[11px]'}`}>
        {config.label}
      </h2>
    </div>
  );
}

export function DemoSoapSection({ letter, label, content, compact = false }) {
  return (
    <div
      className={`rounded-xl border border-gray-200 bg-white shadow-sm ${compact ? 'p-1.5' : 'p-2'}`}
    >
      <div className={`flex items-center justify-between gap-1 ${compact ? 'mb-0.5' : 'mb-1'}`}>
        <span
          className={`flex shrink-0 items-center justify-center rounded-full bg-primary font-bold text-white ${
            compact ? 'h-4 w-4 text-[8px]' : 'h-5 w-5 text-[9px]'
          }`}
        >
          {letter}
        </span>
        <span className="flex-1 text-right text-[9px] font-semibold text-gray-700">{label}</span>
        <X className="h-3 w-3 shrink-0 text-primary" strokeWidth={2} />
      </div>
      <p className="whitespace-pre-wrap text-[8px] leading-snug text-gray-600">{content}</p>
    </div>
  );
}

export function DemoFollowUpTasks({ tasks = [], compact = false }) {
  return (
    <div className={`card-surface w-full ${compact ? 'p-1.5' : 'p-2'}`}>
      <div className={`flex items-center justify-between ${compact ? 'mb-1' : 'mb-1.5'}`}>
        <h3 className="text-[9px] font-semibold text-gray-800">Follow-up tasks</h3>
        <span className="text-[8px] font-semibold text-primary">+ Add</span>
      </div>
      {tasks.length === 0 ? (
        <p className="text-[8px] text-gray-500">No follow-up tasks detected.</p>
      ) : (
        <ul className="space-y-1">
          {tasks.map((task) => (
            <li key={task} className="flex items-center gap-1">
              <input
                readOnly
                value={task}
                className={`flex-1 rounded-lg border border-gray-200 px-2 text-[8px] text-gray-700 ${
                  compact ? 'py-0.5' : 'py-1'
                }`}
              />
              <span className="shrink-0 text-[8px] font-medium text-critical">Remove</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function DemoVitalCard({ label, value, unit, vitalKey = 'bp', compact = false }) {
  const icons = {
    bp: Heart,
    pulse: Activity,
    pain: Gauge,
  };
  const Icon = icons[vitalKey] || Heart;

  return (
    <div
      className={`rounded-xl border border-[#1E40AF] bg-white shadow-sm ${compact ? 'p-1.5' : 'p-2'}`}
    >
      <div className="flex items-center justify-between gap-1 text-gray-500">
        <div className="flex min-w-0 items-center gap-1">
          <Icon className="h-3 w-3 shrink-0 text-primary" strokeWidth={2} />
          <span className="truncate text-[9px]">{label}</span>
        </div>
        <X className="h-3 w-3 text-primary" strokeWidth={2} />
      </div>
      <div className={`flex items-baseline gap-0.5 ${compact ? 'mt-0.5' : 'mt-1'}`}>
        <span
          className={`font-bold leading-none text-gray-900 ${
            compact ? 'text-[13px]' : 'text-[16px]'
          }`}
        >
          {value}
        </span>
        {unit && <span className="text-[8px] text-gray-400">{unit}</span>}
      </div>
    </div>
  );
}

export function DemoMedicationCard({ drugName, dose, route, response }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
      <div className="flex items-center justify-between gap-1 text-gray-500">
        <div className="flex min-w-0 items-center gap-1">
          <Pill className="h-3 w-3 shrink-0 text-primary" strokeWidth={2} />
          <span className="truncate text-[9px]">Medication</span>
        </div>
        <X className="h-3 w-3 text-primary" strokeWidth={2} />
      </div>
      <p className="mt-1.5 text-[13px] font-bold leading-tight text-gray-900">{drugName}</p>
      <div className="mt-1.5 flex flex-wrap gap-1">
        {dose && (
          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[8px] font-medium text-gray-600">
            {dose}
          </span>
        )}
        {route && (
          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[8px] font-medium text-gray-600">
            {route}
          </span>
        )}
      </div>
      {response && <p className="mt-1 text-[8px] text-gray-500">{response}</p>}
    </div>
  );
}

export function DemoActionBar({ saving = false }) {
  return (
    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-3 py-2">
      <button
        type="button"
        className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500"
      >
        <Trash2 className="h-4 w-4" />
      </button>
      <button
        type="button"
        className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white shadow-md"
      >
        <Mic className="h-4 w-4" />
      </button>
      <button
        type="button"
        className={`flex h-9 w-9 items-center justify-center rounded-full bg-success text-white transition-transform ${
          saving ? 'scale-110 shadow-md' : ''
        }`}
      >
        <Check className="h-5 w-5" strokeWidth={2.5} />
      </button>
    </div>
  );
}

export function DemoReviewScreen({ noteType, saving, children }) {
  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-[#f9fafb]">
      <DemoPatientHeader
        name="Nour Al-Deen"
        room="21"
        noteType={noteType === 'vitals' ? 'Vitals' : 'MAR'}
      />
      <div className="flex-1 overflow-hidden px-3 py-2">{children}</div>
      <DemoActionBar saving={saving} />
    </div>
  );
}

export function DemoRecordingOverlay({ transcript, progress, recording }) {
  const shown = transcript.slice(0, progress);

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-[#464E59]">
      <div className="flex flex-1 items-center justify-center px-3 pb-12 pt-5">
        {shown ? (
          <p className="text-center text-[9px] leading-relaxed text-white">{shown}</p>
        ) : (
          <p className="text-[8px] text-white/40">Connecting…</p>
        )}
      </div>
      <p className="absolute bottom-11 left-0 right-0 text-center text-[8px] text-white/70">
        {recording ? 'Release to finish' : 'Hold to record'}
      </p>
      <div className="absolute bottom-1.5 left-1/2 z-10 -translate-x-1/2">
        <div className="relative">
          {recording && (
            <div className="onboarding-pulse-ring-primary absolute -inset-1 rounded-full" />
          )}
          <div
            className={`relative flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white shadow-fab ${
              recording ? 'scale-95' : ''
            }`}
          >
            <Mic className="h-5 w-5" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function DemoPatientListCard({ patient, favorite, selected, showTap }) {
  const name = patient.nameEn;
  const initial = name.charAt(0);

  return (
    <div
      className={`card-surface relative flex items-center gap-2 p-2 transition-all ${
        selected ? 'border-primary-ring' : ''
      }`}
    >
      {showTap && (
        <span className="onboarding-pulse-ring-primary pointer-events-none absolute -right-0.5 -top-0.5 z-10 h-5 w-5 rounded-full" />
      )}
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-primary-mid bg-primary-light/50 text-[10px] font-bold text-primary">
        {initial}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[11px] font-bold text-gray-900">{name}</p>
        <p className="text-[9px] text-gray-500">Room {patient.room}</p>
        <p className="text-[9px] text-gray-500">{patient.ward}</p>
      </div>
      <Star
        className={`h-3.5 w-3.5 shrink-0 ${
          favorite ? 'fill-favorite text-favorite' : 'text-gray-300'
        }`}
      />
    </div>
  );
}

export function DemoListRow({ initial, title, subtitle, badge }) {
  return (
    <div className="card-surface flex items-center gap-2 p-2">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-mid text-[10px] font-bold text-primary">
        {initial}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold">{title}</p>
        {badge && (
          <span className="mt-0.5 inline-block rounded-pill bg-primary px-1.5 py-0.5 text-[7px] font-semibold text-white">
            {badge}
          </span>
        )}
        <p className="text-[8px] text-gray-500">{subtitle}</p>
      </div>
      <span className="rounded-full bg-gray-100 px-2 py-1 text-[8px] font-semibold">View</span>
    </div>
  );
}

export function DemoBottomNav({ activeIndex }) {
  const tabs = [
    { label: 'Patients', icon: Users },
    { label: 'Notes', icon: ClipboardList },
    { label: 'Vitals', icon: HeartPulse },
    { label: 'Meds', icon: Pill },
    { label: 'Reports', icon: ClipboardList },
  ];

  return (
    <div className="flex border-t border-gray-200 bg-surface px-0.5 py-1">
      {tabs.map(({ label, icon: Icon }, index) => (
        <div
          key={label}
          className={`flex flex-1 flex-col items-center gap-0.5 py-0.5 ${
            index === activeIndex ? 'text-navy' : 'text-gray-400'
          }`}
        >
          <Icon className="h-3.5 w-3.5" strokeWidth={2} />
          <span className="text-[6px] font-semibold">{label}</span>
        </div>
      ))}
    </div>
  );
}

export {
  Activity,
  AlertTriangle,
  ArrowLeft,
  ClipboardList,
  Download,
  Moon,
  Pill,
  RefreshCw,
  Send,
  Stethoscope,
};
